import React from 'react';

import Grid from '@mui/material/Grid2';

import { 
  AttachedTable,
  SpellInfusionsFeature,
  Spellbook,
  UpgradableNumber
} from "@dndModels";
import { 
  ABILITY_SCORES, 
  PERCENTAGE_LEVEL_OPTIONS, 
  SCHOOLS,
  SPELLCASTING_FOCUSES
} from "@dndModels/Constants";

import UpgradableNumberBox from "@dndComponents/UpgradableNumberBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import StringBox from "@components/input/strings/StringBox";
import CheckBox from "@components/input/CheckBox";

import SpellbookInput from "@dndComponents/feature/SpellBookInput";
import SelectObjectBox from "@components/input/selects/SelectObjectBoxSimple";
import { AbilityScoreExpandedType } from "@dndModels/Types";

interface Props {
  obj: SpellInfusionsFeature;
  onChange: (changed: SpellInfusionsFeature) => void; 
  disabled: boolean;
  attached_table?: AttachedTable;
}

export default function SpellInfusionsFeatureInput({
  obj,
  onChange,
  disabled,
  attached_table
}: Props) {

  const toFractionString = (level: number) => {
    if (level === 1) {
      return "1";
    } else if (level === (1/2)) {
      return "1/2";
    } else if (level === (1/3)) {
      return "1/3";
    } else if (level === (1/4)) {
      return "1/4";
    }
    return "";
  }

  return (
    <Grid container spacing={0} direction="row">
      <Grid
        size={{
          xs: 6
        }}>
        <SelectStringBox 
          name="Spellcasting Ability" 
          options={[...ABILITY_SCORES,"Selectable"]}
          value={obj.ability} 
          disabled={disabled}
          onChange={(changed: AbilityScoreExpandedType) => {
            const new_obj = obj;
            new_obj.ability = changed;
            onChange(new_obj);
          }} 
        />
      </Grid>
      <Grid
        size={{
          xs: 6
        }}>
        <SelectObjectBox 
          name="Table" 
          data_type="spell_slot_type"
          display_format="{obj.true_name}"
          value={obj.table} 
          disabled={disabled}
          onChange={(id: string) => {
            const new_obj = obj;
            new_obj.table = id;
            onChange(new_obj);
          }} 
        />
      </Grid>
      { obj.ability === "Selectable" &&
        <Grid
          size={{
            xs: 12
          }}>
          <StringBox
            name="Spellcasting Ability Selectable Key"
            value={obj.ability_selectable_key}
            disabled={disabled}
            onBlur={(changed: string) => {
              const new_obj = obj;
              new_obj.ability_selectable_key = changed;
              onChange(new_obj);
            }}
          />
        </Grid>
      }
      <Grid
        size={{
          xs: 6
        }}>
        <SelectStringBox 
          name="Level" 
          options={PERCENTAGE_LEVEL_OPTIONS}
          value={toFractionString(obj.level)} 
          disabled={disabled}
          onChange={(changed: string) => {
            const new_obj = obj;
            switch (changed) {
              case "1": new_obj.level = 1; break;
              case "1/2": new_obj.level = 1/2; break;
              case "1/3": new_obj.level = 1/3; break;
              case "1/4": new_obj.level = 1/4; break;
            }
            onChange(new_obj);
          }} 
        />
      </Grid>
      <Grid
        size={{
          xs: 6
        }}>
        <StringBox 
          name="Level Bonus" 
          value={`${obj.level_bonus}`} 
          disabled={disabled}
          type="number"
          onBlur={(changed: string) => {
            const new_obj = obj;
            new_obj.level_bonus = +changed;
            onChange(new_obj);
          }} 
        />
      </Grid>
      <Grid
        size={{
          xs: 12
        }}>
        <SelectStringBox 
          name="Spellcasting Focus" 
          options={SPELLCASTING_FOCUSES}
          value={obj.focus} 
          disabled={disabled}
          onChange={(changed: string) => {
            const new_obj = obj;
            new_obj.focus = changed;
            onChange(new_obj);
          }} 
        />
      </Grid>
      <Grid
        size={{
          xs: 12
        }}>
        <SpellbookInput
          obj={obj.spellbook} 
          disabled={disabled}
          attached_table={attached_table}
          onChange={(changed: Spellbook) => {
            const new_obj = obj;
            new_obj.spellbook = changed;
            onChange(new_obj);
          }} 
        />
      </Grid>
      <Grid
        size={{
          xs: 6
        }}>
        <UpgradableNumberBox 
          name="Cantrips"
          value={obj.cantrips_max} 
          disabled={disabled}
          attached_table={attached_table}
          onChange={(changed: UpgradableNumber) => {
            const new_obj = obj;
            new_obj.cantrips_max = changed;
            onChange(new_obj);
          }} 
        />
      </Grid>
      <Grid
        size={{
          xs: 6
        }}>
        <UpgradableNumberBox 
          name="Max Infusions per Item"
          value={obj.max_infusions_per_item} 
          disabled={disabled}
          attached_table={attached_table}
          onChange={(changed: UpgradableNumber) => {
            const new_obj = obj;
            new_obj.max_infusions_per_item = changed;
            onChange(new_obj);
          }} 
        />
      </Grid>
      <Grid
        size={{
          xs: 4
        }}>
        <CheckBox 
          name="Ritual Casting" 
          value={obj.ritual_casting} 
          disabled={disabled}
          onChange={(changed: boolean) => {
            const new_obj = obj;
            new_obj.ritual_casting = changed;
            onChange(new_obj);
          }} 
        />
      </Grid>
      <Grid
        size={{
          xs: 4
        }}>
        <SelectObjectBox
          name="Use Spells from List" 
          data_type="spell_list"
          display_format="{obj.true_name}"
          values={ obj.spell_list_ids } 
          extra_options={["Selectable"]}
          multiple
          disabled={disabled}
          onChange={(changed: string[]) => {
            const new_obj = obj;
            new_obj.spell_list_ids = changed;
            onChange(new_obj);
          }} 
        />
      </Grid>
      <Grid
        size={{
          xs: 4
        }}>
        <SelectStringBox 
          name="Magic Schools"
          values={ obj.schools }
          options={SCHOOLS}
          extra_options={["Any"]}
          multiple
          onChange={(changed: string[]) => {
            const new_obj = obj;
            new_obj.schools = changed;
            onChange(new_obj);
          }}
        />
      </Grid>
      { obj.spell_list_ids.includes("Selectable") &&
        <Grid
          size={{
            xs: 12
          }}>
          <StringBox 
            value={obj.spell_list_selectable_key} 
            name="Selectable Key"
            disabled={disabled}
            onBlur={(changed: string) => {
              const new_obj = obj;
              new_obj.spell_list_selectable_key = changed;
              onChange(new_obj);
            }}
          />
        </Grid>
      }
    </Grid>
  );
}

